import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;

/** Current plan / trial / usage snapshot for the signed-in author. */
export function useSubscription() {
  const userId = authStorage.getAuthorUser()?.id;
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/subscription`, { params: { user_id: userId } });
      setSub(data);
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { reload(); }, [reload]);

  return { sub, loading, reload };
}

/** Account nudges: trial ending soon, trial just expired, quota at 80% or exhausted. */
export function useBillingNotices() {
  const userId = authStorage.getAuthorUser()?.id;
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    const fetchNotices = async () => {
      try {
        const { data } = await axios.get(`${API}/notices`, { params: { user_id: userId } });
        if (!alive) return;
        const list = data.notices || [];
        setNotices(list);
        const quota = list.find((n) => n.kind?.startsWith('quota_'));
        if (quota && window.sessionStorage.getItem(`notice-${quota.kind}`) !== '1') {
          window.sessionStorage.setItem(`notice-${quota.kind}`, '1');
          toast.warning(`${quota.title}. ${quota.body}`, {
            action: { label: 'See plans', onClick: () => window.location.assign('/thesis/billing') },
          });
        }
      } catch {
        if (alive) setNotices([]);
      }
    };
    fetchNotices();
    const timer = setInterval(fetchNotices, 120000);
    return () => { alive = false; clearInterval(timer); };
  }, [userId]);

  return notices;
}

/** Short human label + tone for headers and badges. */
export function planSummary(sub) {
  if (!sub) return null;
  const limit = sub.limits?.generations_per_month ?? -1;
  const used = sub.usage?.generations_per_month ?? 0;
  const quota = limit < 0 ? 'unlimited generations' : `${used}/${limit} generations`;
  if (sub.state === 'trialing') {
    const d = sub.trial?.days_left ?? 0;
    return { label: `Trial · ${d} ${d === 1 ? 'day' : 'days'} left`, detail: quota, tone: 'trial' };
  }
  if (sub.plan === 'free') {
    return { label: `${sub.plan_name} · free`, detail: quota, tone: 'free' };
  }
  return { label: `${sub.plan_name}${sub.state === 'active' ? '' : ` · ${sub.state}`}`, detail: quota, tone: 'paid' };
}
